import React, {
  useImperativeHandle,
  useState,
  useLayoutEffect,
  forwardRef,
  memo,
} from "react";
import PropTypes from "prop-types";
import useModalController from "../../hooks/useModalController";

const ModalControlled = forwardRef(
  ({ Component, renderWhenOpen, mountDelayedTimeout, type, ...props }, ref) => {
    const [dialogProps, showModal] = useModalController(type);
    const [[mountDelayed, delayedProps], setMountDelayed] = useState([
      dialogProps.open,
      dialogProps,
    ]);

    useLayoutEffect(() => {
      if (renderWhenOpen) {
        if (dialogProps.open === mountDelayed) return;

        if (dialogProps.open) {
          setMountDelayed([dialogProps.open, dialogProps]);
        }

        const timeoutId = setTimeout(() => {
          setMountDelayed([false, {}]);
        }, mountDelayedTimeout);

        return () => clearTimeout(timeoutId);
      }
      // don't add dialogProps to deps this done to trigger effect efficient
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderWhenOpen, dialogProps.open, mountDelayed, mountDelayedTimeout]);

    useImperativeHandle(
      ref,
      () => ({
        showModal,
        open: dialogProps.open,
        onConfirm: dialogProps.onConfirm,
        onClose: dialogProps.onClose,
      }),
      [dialogProps.onClose, dialogProps.onConfirm, dialogProps.open, showModal]
    );

    if (renderWhenOpen) {
      return mountDelayed ? (
        <Component {...props} {...delayedProps} {...dialogProps} />
      ) : null;
    }

    return <Component {...props} {...dialogProps} />;
  }
);

ModalControlled.defaultProps = {
  mountDelayedTimeout: 500,
};

ModalControlled.propTypes = {
  Component: PropTypes.any,
  renderWhenOpen: PropTypes.bool,
  mountDelayedTimeout: PropTypes.number,
};

ModalControlled.displayName = "ModalControlled";

export default memo(ModalControlled);
